import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router';
import { articlesApi } from '../lib/api';
import { Spinner, Alert } from '@rankhelm/ui';
import type { Article, ArticleImage, ArticleStatus } from '@rankhelm/shared';
import {
  ArticleStatusBar,
  ArticleContentTab,
  ArticleMetadataTab,
  ArticleImagesTab,
  ArticleGeoScoreTab,
  ArticleEditToolbar,
} from '../components/articles';
import type { ArticleContent, ArticleSeoMeta, ArticleGeoScore } from '../components/articles';

const API_BASE = import.meta.env.VITE_API_URL || '';

/** Extended article type with optional joined pillar name */
interface ArticleWithJoins extends Article {
  pillar?: string;
}

/** Resolve image URL — handles R2 keys, relative paths, and absolute URLs */
function resolveImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url.startsWith('/') ? url : `/api/images/${url}`}`;
}

type TabKey = 'content' | 'seo' | 'images' | 'score';

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'content', label: 'Content', icon: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z' },
  { key: 'seo', label: 'Metadata', icon: 'M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z' },
  { key: 'images', label: 'Images', icon: 'M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z' },
  { key: 'score', label: 'GEO Score', icon: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z' },
];

export default function ArticleDetail() {
  const { workspaceId, articleId } = useParams<{ workspaceId: string; articleId: string }>();
  const [article, setArticle] = useState<ArticleWithJoins | null>(null);
  const [images, setImages] = useState<ArticleImage[]>([]);
  const [content, setContent] = useState<ArticleContent | null>(null);
  const [seoMeta, setSeoMeta] = useState<ArticleSeoMeta | null>(null);
  const [geoScore, setGeoScore] = useState<ArticleGeoScore | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('content');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editContent, setEditContent] = useState<ArticleContent | null>(null);
  const [editSeoMeta, setEditSeoMeta] = useState<ArticleSeoMeta | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadArticle = useCallback(async () => {
    if (!articleId) return;
    try {
      const [articleRes, imagesRes] = await Promise.all([
        articlesApi.get(articleId),
        articlesApi.images(articleId).catch(() => ({ data: [] })),
      ]);
      const a = articleRes.data as ArticleWithJoins;
      setArticle(a);
      setImages((imagesRes.data || []) as ArticleImage[]);

      if (a.content_json) {
        try { setContent(JSON.parse(a.content_json) as ArticleContent); } catch { setContent(null); }
      }
      if (a.seo_meta_json) {
        try { setSeoMeta(JSON.parse(a.seo_meta_json) as ArticleSeoMeta); } catch { setSeoMeta(null); }
      }
      if (a.geo_score_json) {
        try { setGeoScore(JSON.parse(a.geo_score_json) as ArticleGeoScore); } catch { setGeoScore(null); }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load article');
    } finally {
      setLoading(false);
    }
  }, [articleId]);

  useEffect(() => { loadArticle(); }, [loadArticle]);

  // Poll while generating or publishing
  useEffect(() => {
    if (!article || (article.status !== 'generating' && article.status !== 'publishing')) return;
    const interval = setInterval(loadArticle, 4000);
    return () => clearInterval(interval);
  }, [article, loadArticle]);

  const handleEditToggle = () => {
    if (!isEditMode) {
      setEditContent(JSON.parse(JSON.stringify(content)) as ArticleContent);
      setEditSeoMeta(JSON.parse(JSON.stringify(seoMeta)) as ArticleSeoMeta);
    }
    setIsEditMode(!isEditMode);
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    setError('');
    try {
      await articlesApi.update(articleId!, {
        content_json: JSON.stringify(editContent),
        seo_meta_json: JSON.stringify(editSeoMeta),
      });
      setSuccessMsg('Changes saved successfully');
      setTimeout(() => setSuccessMsg(''), 3000);
      await loadArticle();
      setIsEditMode(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditContent(null);
    setEditSeoMeta(null);
  };

  const handleStatusChange = async (newStatus: string) => {
    setUpdating(true);
    setError('');
    try {
      if (newStatus === 'publishing') {
        await articlesApi.publish(articleId!);
        setSuccessMsg('Publishing to WordPress...');
      } else {
        await articlesApi.update(articleId!, { status: newStatus as ArticleStatus });
        setSuccessMsg(`Status updated to ${newStatus.replace(/_/g, ' ')}`);
      }
      await loadArticle();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleSelectImage = async (imageId: string) => {
    try {
      await articlesApi.selectImage(articleId!, imageId);
      await loadArticle();
      const imagesRes = await articlesApi.images(articleId!);
      setImages((imagesRes.data || []) as ArticleImage[]);
      setSuccessMsg('Featured image updated');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to select image');
    }
  };

  if (loading) {
    return <Spinner message="Loading article..." fullPage />;
  }

  if (!article) {
    return <div className="p-10 text-red-400 text-center">Article not found</div>;
  }

  const selectedImage = images.find(img => img.is_selected);
  const imageCount = images.length;

  return (
    <div className="px-8 py-6 max-w-[1200px] mx-auto">
      {/* Back to queue link */}
      <Link
        to={`/workspaces/${workspaceId}/articles`}
        className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-helm-400 transition-colors mt-2 mb-1"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
        </svg>
        Back to Article Queue
      </Link>

      {/* Title */}
      <h1 className="text-2xl font-bold text-white mt-3 mb-0">{article.title}</h1>

      {/* Status bar */}
      <ArticleStatusBar
        status={article.status}
        pillar={article.pillar}
        targetKeyword={article.target_keyword}
        wordCount={article.word_count}
        geoScore={article.geo_score}
        updating={updating}
        onStatusChange={handleStatusChange}
      />

      {error && <Alert variant="error">{error}</Alert>}
      {successMsg && <Alert variant="success">{successMsg}</Alert>}

      {/* Generating state */}
      {article.status === 'generating' && (
        <div className="bg-slate-800 border border-yellow-600 rounded-[10px] p-8 text-center mb-6">
          <h3 className="text-yellow-300 m-0 mb-2">Generating Article...</h3>
          <p className="text-slate-400 m-0 text-sm">
            AI is writing your GEO-optimized article in sections. This usually takes 3-4 minutes.
          </p>
          <div className="mt-4 h-1 bg-slate-700 rounded-sm overflow-hidden">
            <div className="h-full bg-yellow-600 rounded-sm w-3/5 animate-[progress_3s_ease-in-out_infinite]" />
          </div>
        </div>
      )}

      {/* Tabs */}
      {content && (
        <>
          <div className="flex border-b border-slate-700 mb-5 justify-between items-center">
            <div className="flex">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 px-5 py-2.5 bg-transparent border-none border-b-2 cursor-pointer text-sm font-medium transition-colors ${
                    activeTab === tab.key
                      ? 'text-blue-400 border-blue-400'
                      : 'text-slate-500 border-transparent hover:text-slate-300'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
                  </svg>
                  {tab.key === 'images' ? `${tab.label} (${imageCount})` : tab.label}
                </button>
              ))}
            </div>
            {['draft', 'in_review', 'revision_requested'].includes(article.status) && (
              <button
                onClick={handleEditToggle}
                className={`px-5 py-2.5 text-white border-none rounded-lg cursor-pointer text-sm font-semibold whitespace-nowrap transition-colors ${
                  isEditMode ? 'bg-red-500 hover:bg-red-400' : 'bg-blue-500 hover:bg-blue-400'
                }`}
              >
                {isEditMode ? 'Cancel Edit' : 'Edit'}
              </button>
            )}
          </div>

          {activeTab === 'content' && (
            <ArticleContentTab
              content={content}
              articleTitle={article.title}
              featuredImageUrl={article.featured_image_url}
              resolveImageUrl={resolveImageUrl}
              isEditMode={isEditMode}
              editContent={editContent}
              setEditContent={setEditContent}
            />
          )}

          {activeTab === 'seo' && seoMeta && (
            <ArticleMetadataTab
              seoMeta={seoMeta}
              selectedImage={selectedImage}
              resolveImageUrl={resolveImageUrl}
              isEditMode={isEditMode}
              editSeoMeta={editSeoMeta}
              setEditSeoMeta={setEditSeoMeta}
            />
          )}

          {activeTab === 'images' && (
            <ArticleImagesTab
              images={images}
              resolveImageUrl={resolveImageUrl}
              onSelectImage={handleSelectImage}
            />
          )}

          {activeTab === 'score' && geoScore && (
            <ArticleGeoScoreTab geoScore={geoScore} />
          )}
        </>
      )}

      {/* Floating Save/Cancel when editing */}
      {isEditMode && (
        <ArticleEditToolbar
          isSaving={isSaving}
          onSave={handleSaveChanges}
          onCancel={handleCancelEdit}
        />
      )}

      <style>{`
        @keyframes progress {
          0% { width: 10%; }
          50% { width: 80%; }
          100% { width: 10%; }
        }
      `}</style>
    </div>
  );
}
